import { useState, useCallback, useRef, useMemo } from "react";
import { getFinalScores, saveFinalScore as saveFinalScoreApi } from "../api/final-scoring-api";
import type { FinalScoringDetail, FinalCriterionScore } from "../types/scoring.type";

interface UseFinalSupplierScoringResult {
	isDirty: boolean;
	isLoading: boolean;
	lastSavedAt: Date | null;
	percentComplete: number;
	scoringDetail: FinalScoringDetail | null;
	getDirtyCriteriaIds: () => number[];
	loadFinalScores: (inviteId: number) => Promise<void>;
	saveFinalScore: (dirtyIds: number[], criteria: FinalCriterionScore[]) => Promise<boolean>;
	updateFinalScore: (
		rfxCriteriaId: number,
		field: "finalScore" | "finalComment",
		value: number | null | string
	) => void;
}

export const useFinalSupplierScoring = (
	rfxId: number,
	evalId: number,
	inviteId: number
): UseFinalSupplierScoringResult => {
	const [scoringDetail, setScoringDetail] = useState<FinalScoringDetail | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
	const dirtyFields = useRef<Set<number>>(new Set());
	const [dirtyVersion, setDirtyVersion] = useState(0);

	const isDirty = dirtyFields.current.size > 0;

	const percentComplete = useMemo(() => {
		if (!scoringDetail || scoringDetail.criteria.length === 0) {
			return 0;
		}
		const scored = scoringDetail.criteria.filter((c) => c.finalScore !== null).length;
		return Math.round((scored / scoringDetail.criteria.length) * 100);
	}, [scoringDetail]);

	const loadFinalScores = useCallback(
		async (loadInviteId: number) => {
			if (!rfxId || !evalId) {
				return;
			}
			setIsLoading(true);
			dirtyFields.current.clear();
			setDirtyVersion((v) => v + 1);
			try {
				const detail = await getFinalScores(rfxId, evalId, loadInviteId);
				setScoringDetail(detail);
			} catch {
				setScoringDetail(null);
			} finally {
				setIsLoading(false);
			}
		},
		[rfxId, evalId]
	);

	const updateFinalScore = useCallback(
		(
			rfxCriteriaId: number,
			field: "finalScore" | "finalComment",
			value: number | null | string
		) => {
			setScoringDetail((prev) => {
				if (!prev) {
					return prev;
				}
				const updatedCriteria = prev.criteria.map((c) => {
					if (c.rfxCriteriaId !== rfxCriteriaId) {
						return c;
					}
					if (field === "finalScore") {
						return { ...c, finalScore: value as number | null };
					}
					return { ...c, finalComment: value as string | null };
				});
				return { ...prev, criteria: updatedCriteria };
			});
			dirtyFields.current.add(rfxCriteriaId);
			setDirtyVersion((v) => v + 1);
		},
		[]
	);

	const getDirtyCriteriaIds = useCallback((): number[] => {
		return Array.from(dirtyFields.current);
	}, []);

	const saveFinalScore = useCallback(
		async (dirtyIds: number[], criteria: FinalCriterionScore[]): Promise<boolean> => {
			if (!rfxId || !evalId || !scoringDetail) {
				return false;
			}
			const dirtyCriteria = dirtyIds
				.map((id) => criteria.find((c) => c.rfxCriteriaId === id))
				.filter((c): c is FinalCriterionScore => c !== undefined);

			if (dirtyCriteria.length === 0) {
				return true;
			}

			try {
				await Promise.all(
					dirtyCriteria.map((c) =>
						saveFinalScoreApi(rfxId, evalId, scoringDetail.inviteId, {
							rfx_criteria_id: c.rfxCriteriaId,
							final_score: c.finalScore,
							final_comment: c.finalComment,
						})
					)
				);
				for (const id of dirtyIds) {
					dirtyFields.current.delete(id);
				}
				setDirtyVersion((v) => v + 1);
				setLastSavedAt(new Date());
				return true;
			} catch {
				return false;
			}
		},
		[rfxId, evalId, scoringDetail]
	);

	// suppress unused variable warning
	void dirtyVersion;

	return {
		scoringDetail,
		isLoading,
		isDirty,
		lastSavedAt,
		percentComplete,
		getDirtyCriteriaIds,
		loadFinalScores,
		updateFinalScore,
		saveFinalScore,
	};
};
